<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';

const props = defineProps({
  credential: { type: Object, default: null },
  templates: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const dialogRef = ref(null);
const selectedMode = ref('');
const form = reactive({
  name: '',
  provider: 'custom',
  credential_type: 'http',
  authentication_type: '',
  status: 'active',
  base_url: '',
  fields: [],
});

const authenticationDefinitions = [
  ['api_key', 'i-lucide-key-round'],
  ['bearer_token', 'i-lucide-fingerprint'],
  ['basic_auth', 'i-lucide-user-round'],
  ['oauth2_client_credentials', 'i-lucide-shield-check'],
  ['no_auth', 'i-lucide-unlock'],
  ['custom', 'i-lucide-sliders-horizontal'],
];
const authenticationTypeCards = computed(() =>
  authenticationDefinitions.map(([value, icon]) => ({
    value,
    icon,
    label: t(`EXTERNAL_CREDENTIALS.AUTHENTICATION_TYPES.${value}`),
    description: t(
      `EXTERNAL_CREDENTIALS.AUTHENTICATION_TYPE_DESCRIPTIONS.${value}`
    ),
  }))
);
const templateIcons = {
  postgresql: 'i-lucide-database',
  mysql: 'i-lucide-database-zap',
  supabase: 'i-lucide-panels-top-left',
  rabbitmq: 'i-lucide-rabbit',
};
const templateCards = computed(() =>
  props.templates.map(template => ({
    ...template,
    icon: templateIcons[template.id] || 'i-lucide-plug',
    label: t(`EXTERNAL_CREDENTIALS.TEMPLATES.${template.id}.TITLE`),
    description: t(`EXTERNAL_CREDENTIALS.TEMPLATES.${template.id}.DESCRIPTION`),
  }))
);
const selectedTemplate = computed(() =>
  props.templates.find(template => template.provider === form.provider)
);
const selectedAuthenticationType = computed(() =>
  authenticationTypeCards.value.find(
    item => item.value === form.authentication_type
  )
);
const selectedIcon = computed(
  () =>
    templateIcons[selectedTemplate.value?.id] ||
    selectedAuthenticationType.value?.icon
);
const isEditMode = computed(() => Boolean(props.credential));
const isTemplate = computed(() => Boolean(selectedTemplate.value));
const isGuidedConfiguration = computed(
  () => isTemplate.value || form.authentication_type !== 'custom'
);
const requiresBaseUrl = computed(
  () => selectedTemplate.value?.base_url_required || false
);
const providerOptions = computed(() =>
  ['custom', 'stripe', 'bubble', 'resend'].map(value => ({
    value,
    label: t(`EXTERNAL_CREDENTIALS.PROVIDERS.${value}`),
  }))
);
const statusOptions = computed(() =>
  ['active', 'disabled'].map(value => ({
    value,
    label: t(`EXTERNAL_CREDENTIALS.STATUSES.${value}`),
  }))
);
const fieldTypeOptions = computed(() =>
  ['secret', 'text', 'url'].map(value => ({
    value,
    label: t(`EXTERNAL_CREDENTIALS.FIELD_TYPES.${value}`),
  }))
);
const deliveryOptions = computed(() =>
  ['none', 'header', 'query', 'body'].map(value => ({
    value,
    label: t(`EXTERNAL_CREDENTIALS.DELIVERY.${value}`),
  }))
);
const title = computed(() =>
  t(
    isEditMode.value
      ? 'EXTERNAL_CREDENTIALS.EDIT_TITLE'
      : 'EXTERNAL_CREDENTIALS.CREATE_TITLE'
  )
);

const authenticationPresets = {
  api_key: [{ key: 'api_key', data_type: 'secret', delivery: 'header' }],
  bearer_token: [{ key: 'token', data_type: 'secret', delivery: 'header' }],
  basic_auth: [
    { key: 'username', data_type: 'secret', delivery: 'none' },
    { key: 'password', data_type: 'secret', delivery: 'none' },
  ],
  oauth2_client_credentials: [
    { key: 'client_id', data_type: 'secret', delivery: 'none' },
    { key: 'client_secret', data_type: 'secret', delivery: 'none' },
    { key: 'token_url', data_type: 'url', delivery: 'none' },
  ],
  no_auth: [],
};

const fieldLabel = key => t(`EXTERNAL_CREDENTIALS.FIELDS.${key}`);
const normalizeField = (field, position) => ({
  id: field.id,
  key: field.key || '',
  label: field.label || fieldLabel(field.key),
  data_type: field.data_type || 'secret',
  delivery: field.delivery || 'none',
  position,
  value: field.configured ? '' : field.default || '',
  configured: field.configured || false,
  required: field.required ?? true,
  options: field.options || null,
  custom: field.custom || false,
  _destroy: false,
});

const fieldsFromDefinition = (definitions, credentialFields = []) =>
  definitions.map((definition, position) => {
    const existing = credentialFields.find(
      field => field.key === definition.key
    );
    return normalizeField({ ...definition, ...existing }, position);
  });

const resetForm = () => {
  Object.assign(form, {
    name: '',
    provider: 'custom',
    credential_type: 'http',
    authentication_type: '',
    status: 'active',
    base_url: '',
    fields: [],
  });
  selectedMode.value = '';
};

const applyCredential = credential => {
  resetForm();
  if (!credential) return;

  const template = props.templates.find(
    item => item.provider === credential.provider
  );
  const presetKeys = new Set(
    (authenticationPresets[credential.authentication_type] || []).map(
      field => field.key
    )
  );
  Object.assign(form, {
    name: credential.name || '',
    provider: credential.provider || 'custom',
    credential_type: credential.credential_type || 'http',
    authentication_type: credential.authentication_type || 'custom',
    status: credential.status || 'active',
    base_url: credential.base_url || '',
    fields: template
      ? fieldsFromDefinition(template.fields, credential.fields || [])
      : (credential.fields || []).map((field, position) =>
          normalizeField(
            {
              ...field,
              custom:
                credential.authentication_type === 'custom' ||
                !presetKeys.has(field.key),
            },
            position
          )
        ),
  });
  selectedMode.value = template ? 'template' : 'authentication';
};

const selectTemplate = template => {
  Object.assign(form, {
    name: template.label,
    provider: template.provider,
    credential_type: template.credential_type,
    authentication_type: template.authentication_type,
    base_url: '',
    fields: fieldsFromDefinition(template.fields),
  });
  selectedMode.value = 'template';
};

const selectAuthenticationType = authenticationType => {
  const definitions = authenticationPresets[authenticationType.value] || [];
  Object.assign(form, {
    name: authenticationType.label,
    provider: 'custom',
    credential_type: 'http',
    authentication_type: authenticationType.value,
    base_url: '',
    fields: fieldsFromDefinition(definitions),
  });
  selectedMode.value = 'authentication';
};

const addField = () => {
  form.fields.push(
    normalizeField(
      {
        key: `field_${form.fields.length + 1}`,
        label: t('EXTERNAL_CREDENTIALS.NEW_FIELD'),
        custom: true,
      },
      form.fields.length
    )
  );
};

const removeField = field => {
  if (field.id) Reflect.set(field, '_destroy', true);
  else form.fields.splice(form.fields.indexOf(field), 1);
};

const valid = computed(() => {
  if (!form.name.trim() || !form.authentication_type) return false;
  if (requiresBaseUrl.value && !form.base_url.trim()) return false;

  return form.fields
    .filter(field => !Reflect.get(field, '_destroy') && field.required)
    .every(field => field.value || field.configured);
});

const save = () => {
  if (!valid.value) return;

  emit('save', {
    name: form.name.trim(),
    provider: form.provider,
    credential_type: form.credential_type,
    authentication_type: form.authentication_type,
    status: form.status,
    base_url: form.base_url.trim(),
    fields: form.fields
      .filter(field => field.id || field.value || field.required)
      .map((field, position) => ({
        id: field.id,
        key: field.key.trim(),
        label: field.label.trim(),
        data_type: field.data_type,
        delivery: field.delivery,
        position,
        value: field.value,
        _destroy: Reflect.get(field, '_destroy'),
      })),
  });
};

watch(
  () => [props.credential, props.templates],
  () => applyCredential(props.credential),
  { immediate: true, deep: true }
);

nextTick(() => dialogRef.value?.open());
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="title"
    :description="t('EXTERNAL_CREDENTIALS.DIALOG_DESCRIPTION')"
    :confirm-button-label="t('EXTERNAL_CREDENTIALS.SAVE')"
    confirm-button-icon="i-lucide-save"
    cancel-button-icon="i-lucide-x"
    :is-loading="isLoading"
    :show-confirm-button="Boolean(selectedMode)"
    :disable-confirm-button="!valid"
    @confirm="save"
    @close="emit('close')"
  >
    <div v-if="!selectedMode" class="grid gap-6">
      <section class="grid gap-3">
        <div>
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('EXTERNAL_CREDENTIALS.TEMPLATE_SELECTION_TITLE') }}
          </h4>
          <p class="mt-1 text-label-small leading-4 text-n-slate-11">
            {{ t('EXTERNAL_CREDENTIALS.TEMPLATE_SELECTION_DESCRIPTION') }}
          </p>
        </div>
        <div class="grid gap-3 sm:grid-cols-2">
          <Button
            v-for="template in templateCards"
            :key="template.id"
            type="button"
            slate
            ghost
            no-animation
            class="h-auto w-full items-start justify-start gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-4 text-start hover:border-n-brand hover:bg-n-brand/5 focus-visible:border-n-brand"
            @click="selectTemplate(template)"
          >
            <Icon :icon="template.icon" class="mt-0.5 size-5 text-n-brand" />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ template.label }}
              </span>
              <span
                class="mt-1 block text-label-small leading-4 text-n-slate-11"
              >
                {{ template.description }}
              </span>
            </span>
          </Button>
        </div>
      </section>

      <section class="grid gap-3 border-t border-n-weak pt-5">
        <div>
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('EXTERNAL_CREDENTIALS.AUTHENTICATION_TYPE_SELECTION_TITLE') }}
          </h4>
          <p class="mt-1 text-label-small leading-4 text-n-slate-11">
            {{
              t(
                'EXTERNAL_CREDENTIALS.AUTHENTICATION_TYPE_SELECTION_DESCRIPTION'
              )
            }}
          </p>
        </div>
        <div class="grid gap-3 sm:grid-cols-2">
          <Button
            v-for="authenticationType in authenticationTypeCards"
            :key="authenticationType.value"
            type="button"
            slate
            ghost
            no-animation
            class="h-auto w-full items-start justify-start gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-4 text-start hover:border-n-brand hover:bg-n-brand/5 focus-visible:border-n-brand"
            @click="selectAuthenticationType(authenticationType)"
          >
            <Icon
              :icon="authenticationType.icon"
              class="mt-0.5 size-5 text-n-brand"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ authenticationType.label }}
              </span>
              <span
                class="mt-1 block text-label-small leading-4 text-n-slate-11"
              >
                {{ authenticationType.description }}
              </span>
            </span>
          </Button>
        </div>
      </section>
    </div>

    <div v-else class="grid gap-5">
      <div
        class="flex items-center justify-between gap-4 border-b border-n-weak pb-4"
      >
        <div class="flex min-w-0 items-center gap-2">
          <Icon :icon="selectedIcon" class="size-4 shrink-0 text-n-brand" />
          <span class="truncate text-sm font-medium text-n-slate-12">
            {{
              isTemplate
                ? t(`EXTERNAL_CREDENTIALS.TEMPLATES.${form.provider}.TITLE`)
                : selectedAuthenticationType?.label
            }}
          </span>
        </div>
        <Button
          v-if="!isEditMode"
          type="button"
          sm
          slate
          outline
          icon="i-lucide-rotate-ccw"
          :label="t('EXTERNAL_CREDENTIALS.CHANGE_TYPE')"
          @click="resetForm"
        />
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <Input v-model="form.name" :label="t('EXTERNAL_CREDENTIALS.NAME')" />
        <AdaptiveSelect
          v-model="form.status"
          :options="statusOptions"
          :display-label="t('EXTERNAL_CREDENTIALS.STATUS')"
        />
        <AdaptiveSelect
          v-if="!isTemplate && !isEditMode"
          v-model="form.provider"
          :options="providerOptions"
          :display-label="t('EXTERNAL_CREDENTIALS.PROVIDER')"
        />
        <Input
          v-if="form.credential_type === 'http'"
          v-model="form.base_url"
          type="url"
          :label="t('EXTERNAL_CREDENTIALS.BASE_URL')"
          :message="t('EXTERNAL_CREDENTIALS.BASE_URL_HELP')"
        />
      </div>

      <section class="grid gap-3 border-t border-n-weak pt-5">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('EXTERNAL_CREDENTIALS.FIELDS_TITLE') }}
            </h4>
            <p class="mt-1 text-label-small leading-4 text-n-slate-11">
              {{
                t(
                  isGuidedConfiguration
                    ? 'EXTERNAL_CREDENTIALS.GUIDED_FIELDS_DESCRIPTION'
                    : 'EXTERNAL_CREDENTIALS.FIELDS_DESCRIPTION'
                )
              }}
            </p>
          </div>
          <Button
            v-if="!isTemplate"
            type="button"
            sm
            outline
            icon="i-lucide-plus"
            :label="t('EXTERNAL_CREDENTIALS.ADD_FIELD')"
            @click="addField"
          />
        </div>

        <div
          v-for="(field, index) in form.fields.filter(item => !item._destroy)"
          :key="field.id || field.key || index"
          class="grid gap-3 rounded-lg border border-n-weak p-3 md:grid-cols-2"
        >
          <template v-if="isGuidedConfiguration && !field.custom">
            <AdaptiveSelect
              v-if="field.options"
              v-model="field.value"
              class="md:col-span-2"
              :options="
                field.options.map(value => ({
                  value,
                  label: t(`EXTERNAL_CREDENTIALS.FIELD_OPTIONS.${value}`),
                }))
              "
              :display-label="fieldLabel(field.key)"
            />
            <Input
              v-else
              v-model="field.value"
              class="md:col-span-2"
              :type="field.data_type === 'secret' ? 'password' : 'text'"
              :label="fieldLabel(field.key)"
              :message="
                field.configured
                  ? t('EXTERNAL_CREDENTIALS.FIELD_VALUE_KEEP')
                  : t('EXTERNAL_CREDENTIALS.FIELD_VALUE_REQUIRED')
              "
            />
          </template>
          <template v-else>
            <Input
              v-model="field.key"
              :label="t('EXTERNAL_CREDENTIALS.FIELD_KEY')"
            />
            <Input
              v-model="field.label"
              :label="t('EXTERNAL_CREDENTIALS.FIELD_LABEL')"
            />
            <AdaptiveSelect
              v-model="field.data_type"
              :options="fieldTypeOptions"
              :display-label="t('EXTERNAL_CREDENTIALS.FIELD_TYPE')"
            />
            <AdaptiveSelect
              v-model="field.delivery"
              :options="deliveryOptions"
              :display-label="t('EXTERNAL_CREDENTIALS.DELIVERY_TITLE')"
            />
            <WithLabel
              class="md:col-span-2"
              :name="`external-credential-field-value-${index}`"
              :label="t('EXTERNAL_CREDENTIALS.FIELD_VALUE')"
              :help-message="
                field.configured
                  ? t('EXTERNAL_CREDENTIALS.FIELD_VALUE_KEEP')
                  : t('EXTERNAL_CREDENTIALS.FIELD_VALUE_REQUIRED')
              "
            >
              <div class="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2">
                <Input
                  v-model="field.value"
                  class="min-w-0 w-full"
                  :type="field.data_type === 'secret' ? 'password' : 'text'"
                />
                <Button
                  type="button"
                  ruby
                  outline
                  icon="i-lucide-trash-2"
                  :aria-label="t('EXTERNAL_CREDENTIALS.REMOVE_FIELD')"
                  @click="removeField(field)"
                />
              </div>
            </WithLabel>
          </template>
        </div>
        <p
          v-if="!form.fields.some(field => !field._destroy)"
          class="text-sm text-n-slate-11"
        >
          {{ t('EXTERNAL_CREDENTIALS.FIELDS_EMPTY') }}
        </p>
      </section>
    </div>
  </Dialog>
</template>
